import 'tippy.js/dist/tippy.css';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Extension,
  JSONContent,
  markInputRule,
  markPasteRule,
} from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import Italic from '@tiptap/extension-italic';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import { keymap } from '@tiptap/pm/keymap';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { tiptapToWhatsapp } from 'utils/tiptapToWhatsapp';
import {
  createWhatsappBoldInputRegex,
  createWhatsappBoldRegex,
  createWhatsappItalicInputRegex,
  createWhatsappItalicRegex,
  createWhatsappStrikeInputRegex,
  createWhatsappStrikeRegex,
} from 'utils/whatsappFormattingRegex';
import { whatsappToHtml } from 'utils/whatsappToHtml';

import { mentionSuggestion, MentionItem } from './mentionSuggestion';

const formattingMarks = [
  { markName: 'bold', createRegex: createWhatsappBoldRegex },
  { markName: 'italic', createRegex: createWhatsappItalicRegex },
  { markName: 'strike', createRegex: createWhatsappStrikeRegex },
] as const;

type FormattingMarkName = (typeof formattingMarks)[number]['markName'];

/** Re-evaluates syntax that becomes valid after Backspace/Delete. */
const ApplyWhatsappFormattingOnEdit = Extension.create({
  name: 'applyWhatsappFormattingOnEdit',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('applyWhatsappFormattingOnEdit'),
        appendTransaction(transactions, _prevState, nextState) {
          if (!transactions.some(transaction => transaction.docChanged)) {
            return null;
          }

          let matchToApply:
            | {
                content: string;
                from: number;
                markName: FormattingMarkName;
              }
            | undefined;

          nextState.doc.descendants((node, position, parent) => {
            if (
              matchToApply ||
              !node.isText ||
              parent?.type.name === 'codeBlock' ||
              node.marks.some(mark => mark.type.name === 'code')
            ) {
              return;
            }

            const nodeText = node.text ?? '';
            formattingMarks.forEach(({ markName, createRegex }) => {
              const match = createRegex().exec(nodeText);
              if (
                match &&
                (!matchToApply || position + match.index < matchToApply.from)
              ) {
                matchToApply = {
                  content: match[1],
                  from: position + match.index,
                  markName,
                };
              }
            });
          });

          if (!matchToApply) return null;

          const markType = nextState.schema.marks[matchToApply.markName];
          if (!markType) return null;

          const transaction = nextState.tr;
          const closingDelimiterPosition =
            matchToApply.from + matchToApply.content.length + 1;
          transaction.delete(
            closingDelimiterPosition,
            closingDelimiterPosition + 1,
          );
          transaction.delete(matchToApply.from, matchToApply.from + 1);
          transaction.addMark(
            matchToApply.from,
            matchToApply.from + matchToApply.content.length,
            markType.create(),
          );
          transaction.removeStoredMark(markType);

          return transaction;
        },
      }),
    ];
  },
});

const WhatsAppBold = Bold.extend({
  addInputRules() {
    return [
      markInputRule({
        find: createWhatsappBoldInputRegex(),
        type: this.type,
      }),
    ];
  },
  addPasteRules() {
    return [
      markPasteRule({
        find: createWhatsappBoldRegex(),
        type: this.type,
      }),
    ];
  },
});

const WhatsAppItalic = Italic.extend({
  addInputRules() {
    return [
      markInputRule({
        find: createWhatsappItalicInputRegex(),
        type: this.type,
      }),
    ];
  },
  addPasteRules() {
    return [
      markPasteRule({
        find: createWhatsappItalicRegex(),
        type: this.type,
      }),
    ];
  },
});

const WhatsAppStrike = Strike.extend({
  addInputRules() {
    return [
      markInputRule({
        find: createWhatsappStrikeInputRegex(),
        type: this.type,
      }),
    ];
  },
  addPasteRules() {
    return [
      markPasteRule({
        find: createWhatsappStrikeRegex(),
        type: this.type,
      }),
    ];
  },
});

const WhatsAppMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      contact: {
        default: null,
        parseHTML: element => element.getAttribute('data-contact'),
        renderHTML: attributes => {
          if (!attributes.contact) return {};
          return { 'data-contact': attributes.contact };
        },
      },
      waPhoneNumberId: {
        default: null,
        parseHTML: element => element.getAttribute('data-wa-phone-number-id'),
        renderHTML: attributes => {
          if (!attributes.waPhoneNumberId) return {};
          return { 'data-wa-phone-number-id': attributes.waPhoneNumberId };
        },
      },
    };
  },
});

function createEnterToSendExtension(
  sendRef: React.MutableRefObject<() => void>,
) {
  return Extension.create({
    name: 'enterToSend',
    addProseMirrorPlugins() {
      return [
        keymap({
          Enter: () => {
            sendRef.current();
            return true;
          },
        }),
      ];
    },
  });
}

const ClearMarksOnEmpty = Extension.create({
  name: 'clearMarksOnEmpty',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('clearMarksOnEmpty'),
        appendTransaction(transactions, prevState, nextState) {
          if (nextState.doc.textContent.length === 0) {
            if (nextState.storedMarks && nextState.storedMarks.length > 0) {
              return nextState.tr.setStoredMarks([]);
            }
          }
          return null;
        },
      }),
    ];
  },
});

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  editorWrapper: {
    flexGrow: 1,
    minWidth: 0,
    '& .ProseMirror': {
      outline: 'none',
      border: 'none',
      color: palette.text.primary,
      fontSize: typography.pxToRem(14),
      lineHeight: 1.5,
      maxHeight: `calc(${typography.pxToRem(14)} * 1.5 * 4)`,
      overflowY: 'auto',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      '& p': { margin: 0 },

      '& p.is-editor-empty:first-child::before': {
        color: palette.text.disabled,
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
      },

      '& .mention': {
        backgroundColor:
          palette.type === 'dark'
            ? 'rgba(179, 136, 255, 0.18)'
            : 'rgba(237, 231, 246, 1)',
        borderRadius: '0.4rem',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
        color:
          palette.type === 'dark' ? 'rgb(206, 147, 216)' : 'rgb(106, 27, 154)',
        paddingInline: spacing(0.5),
        fontWeight: 500,
        cursor: 'default',
        userSelect: 'all',
      },

      '& strong': { fontWeight: 700 },
      '& em': { fontStyle: 'italic' },
      '& s': { textDecoration: 'line-through' },
      '& code': {
        backgroundColor:
          palette.type === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.06)',
        borderRadius: 3,
        padding: '1px 4px',
        fontFamily: 'monospace',
        fontSize: typography.pxToRem(13),
      },
      '& pre': {
        backgroundColor:
          palette.type === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.04)',
        borderRadius: 4,
        padding: spacing(1, 1.5),
        fontFamily: 'monospace',
        fontSize: typography.pxToRem(13),
        '& code': { background: 'none', padding: 0 },
      },
      '& blockquote': {
        borderLeft: `3px solid ${palette.divider}`,
        margin: '4px 0',
        paddingLeft: spacing(1),
        color: palette.text.secondary,
      },
      '& ul, & ol': { paddingLeft: spacing(2.5), margin: '4px 0' },
    },
  },
}));

export interface ChatInputRef {
  setContent: (content: string | JSONContent) => void;
  insertContent: (content: string) => void;
  focus: (position?: 'start' | 'end' | 'all' | number | null) => void;
  clear: () => void;
  send: () => void;
}

export interface ChatInputProps {
  fetchUsers?: (query: string) => Promise<MentionItem[]>;
  disableMentions?: boolean;
  onSend: (text: string, mentions: import('types').ContatoMencionado[]) => void;
  onInputChange?: (
    text: string,
    hasContent: boolean,
    mentions?: import('types').ContatoMencionado[],
  ) => void;
  showModalRespostasProntas?: () => void;
  disabled?: boolean;
  placeholder?: string;
  initialValue?: string;
  autoFocus?: boolean;
  id?: string;
  /** Permite disparar onSend mesmo quando o editor está vazio */
  allowEmptySend?: boolean;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(
  (
    {
      fetchUsers,
      disableMentions = false,
      onSend,
      onInputChange,
      showModalRespostasProntas,
      disabled = false,
      placeholder = 'Digite # para respostas prontas',
      initialValue = '',
      autoFocus = false,
      id,
      allowEmptySend = false,
    },
    ref,
  ) => {
    const classes = useStyles();

    const sendRef = useRef<() => void>(() => undefined);

    const fetchUsersRef = useRef(fetchUsers);
    useEffect(() => {
      fetchUsersRef.current = fetchUsers;
    }, [fetchUsers]);

    const [EnterToSend] = useState(() => createEnterToSendExtension(sendRef));

    const showRespostasRef = useRef<(() => void) | undefined>(undefined);
    useEffect(() => {
      showRespostasRef.current = showModalRespostasProntas;
    });

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          bold: false,
          italic: false,
          strike: false,
          code: false,
        }),
        EnterToSend,
        ClearMarksOnEmpty,
        ApplyWhatsappFormattingOnEdit,
        Placeholder.configure({ placeholder }),
        WhatsAppBold,
        WhatsAppItalic,
        WhatsAppStrike,
        Code,
        ...(!disableMentions && fetchUsers
          ? [
              WhatsAppMention.configure({
                HTMLAttributes: { class: 'mention' },
                renderText({ node }) {
                  return `@${
                    (node.attrs.waPhoneNumberId as string) ||
                    (node.attrs.contact as string) ||
                    (node.attrs.id as string)
                  }`;
                },
                renderHTML({ node }) {
                  return [
                    'span',
                    {
                      class: 'mention',
                      'data-contact': node.attrs.contact,
                      'data-wa-phone-number-id': node.attrs.waPhoneNumberId,
                    },
                    `@${
                      (node.attrs.label as string) ?? (node.attrs.id as string)
                    }`,
                  ];
                },
                suggestion: mentionSuggestion(query => {
                  return fetchUsersRef.current
                    ? fetchUsersRef.current(query)
                    : Promise.resolve([]);
                }),
              }),
            ]
          : []),
      ],
      content: initialValue,
      editable: !disabled,
      editorProps: {
        attributes: {
          ...(id ? { id } : {}),
        },
        handlePaste: (view, event) => {
          const files = event.clipboardData?.files;
          if (files && files.length > 0) {
            return false;
          }
          const text = event.clipboardData?.getData('text/plain');
          if (text) {
            event.preventDefault();
            const html = whatsappToHtml(text);
            const domParser = ProseMirrorDOMParser.fromSchema(
              view.state.schema,
            );
            const element = document.createElement('div');
            element.innerHTML = html;
            const slice = domParser.parseSlice(element);
            view.dispatch(view.state.tr.replaceSelection(slice));
            return true;
          }
          return false;
        },
      },
      onSelectionUpdate: ({ editor: ed }) => {
        if (ed.isFocused) {
          ed.commands.scrollIntoView();
        }
      },
      onUpdate: ({ editor: ed }) => {
        const { text, mentions } = tiptapToWhatsapp(ed.getJSON());
        onInputChange?.(text, !ed.isEmpty, mentions);

        if (text === '#') {
          ed.commands.clearContent(true);
          showRespostasRef.current?.();
        }

        if (ed.isFocused) {
          ed.commands.scrollIntoView();
        }
      },
    });

    useEffect(() => {
      sendRef.current = () => {
        if (!editor) return;
        const { text, mentions } = tiptapToWhatsapp(editor.getJSON());
        const trimmed = text.trim();
        if (trimmed || allowEmptySend) onSend(trimmed, mentions);
      };
    });

    useEffect(() => {
      if (editor && editor.isEditable === disabled) {
        editor.setEditable(!disabled);
      }
    }, [editor, disabled]);

    useEffect(() => {
      if (editor && autoFocus && !disabled) {
        editor.commands.focus('end');
      }
    }, [autoFocus, disabled, editor]);

    useImperativeHandle(ref, () => ({
      setContent: (content: string | JSONContent) => {
        if (typeof content === 'string') {
          editor?.commands.setContent(whatsappToHtml(content));
        } else {
          editor?.commands.setContent(content);
        }
      },
      insertContent: (content: string) => {
        editor?.chain().focus().insertContent(whatsappToHtml(content)).run();
      },
      focus: (position?: 'start' | 'end' | 'all' | number | null) => {
        if (editor) {
          if (editor.isFocused) {
            editor.commands.focus();
          } else {
            editor.commands.focus(position ?? 'end');
          }
        }
      },
      clear: () => {
        editor?.chain().clearContent(true).unsetAllMarks().focus().run();
      },
      send: () => {
        sendRef.current();
      },
    }));

    return (
      <div className={classes.editorWrapper}>
        <EditorContent editor={editor} />
      </div>
    );
  },
);

ChatInput.displayName = 'ChatInput';
